import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert, Badge, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, InputAdornment, List,
  ListItemButton, MenuItem, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import { ArrowRightLeft, Bot, Hand, LogOut, MessagesSquare, Search, Sparkles, Ticket, UserCheck } from "lucide-react";
import { HelpdeskPage, useHd } from "./HelpdeskContext";
import { hd, errorMessage } from "./api";
import { getAgentSocket, useRoom, useSocketEvent } from "./socket";
import MessageThread, { upsertMessage } from "./MessageThread";
import Composer from "./Composer";
import { ModeBadge, PresenceDot, PriorityBadge, SentimentChip, SlaBadge, SmallLabel, StatusBadge } from "./ui";
import { GlassCard, PersonAvatar, T, TYPE, formatRelative, ghostButtonSx, primaryButtonSx } from "../user/complaints/shared";

const GROUPS = [["queue", "Queue"], ["mine", "Mine"], ["live", "All live"], ["ai", "AI"], ["ended", "Ended"]];

const waitLabel = (s) => (s == null ? "" : s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)}m` : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`);

function Chat() {
  const ctx = useHd();
  const [params, setParams] = useSearchParams();
  const selected = params.get("c");
  const [group, setGroup] = useState(params.get("g") || "queue");
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myPresence, setMyPresence] = useState(ctx.me.presence || "online");

  const loadList = useCallback(async () => {
    try {
      const r = await hd.conversations({ group, q: q || undefined, limit: 100 });
      setList(r.rows);
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    } finally {
      setLoading(false);
    }
  }, [group, q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(loadList, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadList, q]);

  // waiting timers tick locally
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((x) => x + 1), 15000);
    return () => clearInterval(t);
  }, []);

  const throttle = useRef(null);
  useSocketEvent(["conversation.created", "conversation.updated", "handoff.requested", "handoff.completed", "visitor.online", "visitor.offline"], (p, ev) => {
    if (ev === "conversation.updated" && p?._id) {
      setList((l) => l.map((c) => (String(c._id) === String(p._id) ? { ...c, ...p } : c)));
    }
    if (throttle.current) return;
    throttle.current = setTimeout(() => {
      throttle.current = null;
      loadList();
    }, 1200);
  });

  useEffect(() => {
    if (ctx.me.presence) setMyPresence(ctx.me.presence);
  }, [ctx.me.presence]);

  const setPresence = async (status) => {
    try {
      await hd.setPresence(status);
      setMyPresence(status);
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };

  const open = (id) => {
    const next = new URLSearchParams(params);
    next.set("c", id);
    setParams(next);
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "300px minmax(0,1fr)", lg: "300px minmax(0,1fr) 310px" }, gap: 1.5, height: "calc(100vh - 48px)" }}>
      <GlassCard sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${T.border}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <MessagesSquare size={18} color={T.brand} />
              <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle }}>Live Chat</Typography>
            </Stack>
            <TextField select size="small" value={myPresence} onChange={(e) => setPresence(e.target.value)} sx={{ width: 120, "& .MuiInputBase-input": { py: 0.5, fontSize: TYPE.micro } }}
              SelectProps={{ renderValue: (v) => <Stack direction="row" spacing={0.75} alignItems="center"><PresenceDot presence={v} /><span style={{ textTransform: "capitalize" }}>{v}</span></Stack> }}>
              {["online", "away", "busy", "offline"].map((s) => <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField size="small" fullWidth placeholder="Search customer or message" value={q} onChange={(e) => setQ(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> }} />
        </Box>
        <Tabs value={group} onChange={(_, v) => { setGroup(v); setLoading(true); }} variant="scrollable" sx={{ minHeight: 36, borderBottom: `1px solid ${T.border}`, "& .MuiTab-root": { minHeight: 36, minWidth: 0, px: 1.5, textTransform: "none", fontSize: TYPE.small, fontWeight: 600 } }}>
          {GROUPS.map(([k, l]) => <Tab key={k} value={k} label={l} />)}
        </Tabs>
        <List sx={{ overflowY: "auto", flexGrow: 1, py: 0 }}>
          {loading && <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={20} /></Box>}
          {!loading && !list.length && <Typography sx={{ p: 3, textAlign: "center", color: T.inkMuted, fontSize: TYPE.small }}>{group === "queue" ? "Nobody is waiting" : "No conversations"}</Typography>}
          {list.map((c) => (
            <ListItemButton key={c._id} selected={String(selected) === String(c._id)} onClick={() => open(c._id)} sx={{ alignItems: "flex-start", borderBottom: `1px solid ${T.border}`, py: 1.25, "&.Mui-selected": { bgcolor: T.brandSoft } }}>
              <Badge overlap="circular" variant="dot" color="success" invisible={!c.visitor?.online} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <PersonAvatar name={c.visitor?.name || c.visitor?.email} size={34} />
              </Badge>
              <Box sx={{ ml: 1.25, minWidth: 0, flexGrow: 1 }}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography noWrap sx={{ fontSize: TYPE.small, fontWeight: c.unreadForAgent ? 800 : 600 }}>{c.visitor?.name || c.visitor?.email || "Visitor"}</Typography>
                  <Typography sx={{ fontSize: "0.68rem", color: c.status === "queued" ? "#B54708" : T.inkMuted, whiteSpace: "nowrap", fontWeight: c.status === "queued" ? 700 : 400 }}>
                    {c.status === "queued" ? `waiting ${waitLabel(c.queuedAt ? Math.round((Date.now() - new Date(c.queuedAt)) / 1000) : c.waitingSeconds)}` : formatRelative(c.lastMessageAt)}
                  </Typography>
                </Stack>
                <Typography noWrap sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{c.lastMessagePreview || "—"}</Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} alignItems="center">
                  {c.mode === "ai" && <Chip size="small" icon={<Bot size={11} />} label="AI" sx={{ height: 18, fontSize: "0.62rem", bgcolor: "#F4EBFF" }} />}
                  {c.ticket?.key && <Chip size="small" label={c.ticket.key} sx={{ height: 18, fontSize: "0.62rem" }} />}
                  {c.assignee?.name && <Typography noWrap sx={{ fontSize: "0.66rem", color: T.inkMuted }}>{c.assignee.name}</Typography>}
                  {c.unreadForAgent > 0 && <Chip size="small" color="primary" label={c.unreadForAgent} sx={{ height: 18, fontSize: "0.62rem", ml: "auto" }} />}
                </Stack>
              </Box>
            </ListItemButton>
          ))}
        </List>
      </GlassCard>

      {selected ? <ActiveChat key={selected} id={selected} onChanged={loadList} /> : (
        <GlassCard sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 4, textAlign: "center" }}>
          <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: "rgba(99, 102, 241, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgb(99, 102, 241)", mb: 2 }}>
            <MessagesSquare size={26} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: T.ink, mb: 0.5 }}>No conversation selected</Typography>
          <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, maxWidth: 360 }}>
            Select an active customer conversation from the queue on the left to start chatting, review message history, or respond.
          </Typography>
        </GlassCard>
      )}
    </Box>
  );
}

function ActiveChat({ id, onChanged }) {
  const ctx = useHd();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [typing, setTyping] = useState("");
  const [transfer, setTransfer] = useState(false);
  const [error, setError] = useState("");
  const typingTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const [conv, m] = await Promise.all([hd.conversation(id), hd.conversationMessages(id, { limit: 50 })]);
      setC(conv);
      setMessages(m.rows);
      setHasMore(m.hasMore);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  useRoom("conversation", id);

  const markRead = useCallback((list) => {
    const lastCustomer = [...list].reverse().find((m) => m.senderType === "CUSTOMER");
    if (lastCustomer && !lastCustomer.readAt) getAgentSocket()?.emit("message.read", { conversationId: id, lastMessageId: lastCustomer._id });
  }, [id]);
  useEffect(() => {
    if (messages.length && document.visibilityState === "visible") markRead(messages);
  }, [messages, markRead]);

  useSocketEvent("message.created", (m) => {
    if (String(m.conversationId) !== String(id)) return;
    setMessages((l) => upsertMessage(l, m));
    if (m.senderType === "CUSTOMER") setTyping("");
  });
  useSocketEvent(["message.read", "message.delivered"], (p, ev) => {
    if (String(p.conversationId) !== String(id)) return;
    setMessages((l) => l.map((m) => {
      if (ev === "message.delivered" && (p.messageIds || []).map(String).includes(String(m._id)) && m.status !== "read") return { ...m, status: "delivered", deliveredAt: p.deliveredAt };
      if (ev === "message.read" && p.by === "CUSTOMER" && m.senderType !== "CUSTOMER" && String(m._id) <= String(p.upToId)) return { ...m, status: "read", readAt: p.readAt };
      return m;
    }));
  });
  useSocketEvent(["typing.started", "typing.stopped"], (p, ev) => {
    if (String(p.conversationId) !== String(id) || p.by === "AGENT") return;
    clearTimeout(typingTimer.current);
    if (ev === "typing.started") {
      setTyping(p.by === "AI" ? "AI is typing…" : "Customer is typing…");
      typingTimer.current = setTimeout(() => setTyping(""), 6000);
    } else setTyping("");
  });
  useSocketEvent(["conversation.updated", "visitor.online", "visitor.offline"], (p, ev) => {
    const cid = p._id || p.conversationId;
    if (String(cid) !== String(id)) return;
    if (ev === "conversation.updated") setC((prev) => (prev ? { ...prev, ...p, visitor: { ...prev.visitor, ...(p.visitor || {}) } } : prev));
    else setC((prev) => (prev ? { ...prev, visitor: { ...prev.visitor, online: ev === "visitor.online" } } : prev));
  });

  const action = async (name, body, msg) => {
    try {
      await hd.chatAction(id, name, body);
      if (msg) ctx.notify(msg);
      await load();
      onChanged();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };

  const onTyping = (on) => getAgentSocket()?.emit(on ? "typing.started" : "typing.stopped", { conversationId: id });

  if (error) return <GlassCard sx={{ p: 3 }}><Alert severity="error">{error}</Alert></GlassCard>;
  if (!c) return <GlassCard sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}><CircularProgress size={24} /></GlassCard>;

  const mine = String(c.assignee?.id) === String(ctx.me.id);
  const ended = c.status === "ended";
  const aiOn = ctx.settings?.ai?.enabled;
  const send = async ({ content, attachmentIds, internal }) => {
    const m = await hd.sendChat(id, { content, attachmentIds, internal, clientMsgId: `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}` });
    setMessages((l) => upsertMessage(l, m));
    if (c.mode === "ai") load();
  };

  return (
    <>
      <GlassCard sx={{ display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
        <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${T.border}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
              <PersonAvatar name={c.visitor?.name || c.visitor?.email} size={34} />
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 700, fontSize: TYPE.body }}>{c.visitor?.name || c.visitor?.email || "Visitor"}</Typography>
                <Typography sx={{ fontSize: TYPE.micro, color: c.visitor?.online ? "#067647" : T.inkMuted }}>{c.visitor?.online ? "Online" : "Offline"}{c.assignee?.name ? ` · ${c.assignee.name}` : ""}</Typography>
              </Box>
              <ModeBadge mode={c.mode} status={c.status} />
            </Stack>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {!ended && ["queued", "assigned", "open"].includes(c.status) && c.mode !== "ai" && <Button size="small" variant="contained" sx={primaryButtonSx} startIcon={<UserCheck size={15} />} onClick={() => action("accept", {}, "Chat accepted")}>Accept</Button>}
              {!ended && c.mode === "ai" && ctx.can("chat.takeover") && <Button size="small" variant="contained" sx={primaryButtonSx} startIcon={<Hand size={15} />} onClick={() => action("takeover", {}, "You took over — AI stopped")}>Take over</Button>}
              {!ended && c.mode !== "ai" && aiOn && ctx.settings?.ai?.chatbotEnabled && ctx.settings?.ai?.allowReturnToAi && ctx.can("chat.takeover") && <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<Bot size={15} />} onClick={() => action("return-to-ai", {}, "Returned to AI")}>Return to AI</Button>}
              {!ended && c.mode !== "ai" && aiOn && ctx.can("ai.use") && (
                <Tooltip title="AI stays silent to the customer and suggests replies to you">
                  <Button size="small" variant="outlined" sx={{ ...ghostButtonSx, ...(c.mode === "ai_assist" ? { color: "#026AA2", borderColor: "#026AA2", bgcolor: "#E0F2FE" } : {}) }} startIcon={<Sparkles size={15} />} onClick={() => action("assist", { enabled: c.mode !== "ai_assist" })}>
                    {c.mode === "ai_assist" ? "AI Assist on" : "AI Assist"}
                  </Button>
                </Tooltip>
              )}
              {!ended && ctx.can("chat.transfer") && <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<ArrowRightLeft size={15} />} onClick={() => setTransfer(true)}>Transfer</Button>}
              {c.ticketId && <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<Ticket size={15} />} onClick={() => navigate(`/helpdesk/tickets/${c.ticketId}`)}>Ticket</Button>}
              {!ended && <Button size="small" color="error" startIcon={<LogOut size={15} />} onClick={() => action("end", {}, "Chat ended")}>End</Button>}
            </Stack>
          </Stack>
        </Box>
        <Box sx={{ flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <MessageThread messages={messages} typing={typing} hasMore={hasMore} height="100%"
            onLoadOlder={async () => { const r = await hd.conversationMessages(id, { before: messages[0]?._id }); setMessages((l) => [...r.rows, ...l]); setHasMore(r.hasMore); }} />
        </Box>
        {ended ? (
          <Alert severity="info" sx={{ m: 2 }}>This conversation has ended.</Alert>
        ) : !c.ticketId ? (
          <Alert severity="info" sx={{ m: 2 }}>Waiting for the customer's first message.</Alert>
        ) : (
          <Composer ticketId={c.ticketId} onSend={send} onTyping={onTyping} showStatus={false}
            vars={{ customer_name: c.visitor?.name || "", ticket_id: c.ticket?.key || "", agent_name: ctx.me.name }}
            placeholder={c.mode === "ai" ? "Sending a message takes over from the AI…" : !mine && c.status === "active" ? `${c.assignee?.name || "Another agent"} is handling this chat…` : "Type a message…"} />
        )}
      </GlassCard>
      <CustomerSidebar c={c} onLinked={load} />
      {transfer && <TransferDialog onClose={() => setTransfer(false)} onDone={async (body) => { await action("transfer", body, "Chat transferred"); setTransfer(false); }} />}
    </>
  );
}

function CustomerSidebar({ c, onLinked }) {
  const ctx = useHd();
  const navigate = useNavigate();
  const [crm, setCrm] = useState(null);
  useEffect(() => {
    if (c.contactId) hd.customer(c.contactId).then(setCrm).catch(() => setCrm(null));
  }, [c.contactId]);
  const t = c.ticket;
  const Row = ({ label, value }) => (
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.6 }}>
      <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{label}</Typography>
      <Typography sx={{ fontSize: TYPE.micro, fontWeight: 600, textAlign: "right", maxWidth: 180 }} noWrap>{value ?? "—"}</Typography>
    </Stack>
  );
  return (
    <GlassCard sx={{ p: 2, overflowY: "auto", display: { xs: "none", lg: "block" } }}>
      <SmallLabel sx={{ mb: 1 }}>Customer</SmallLabel>
      <Row label="Name" value={c.visitor?.name || (c.contact && `${c.contact.firstName} ${c.contact.lastName || ""}`)} />
      <Row label="Email" value={c.visitor?.email || c.contact?.email} />
      <Row label="Phone" value={c.visitor?.phone || c.contact?.phone} />
      <Row label="Company" value={c.contact?.company?.name} />
      {c.visitor?.pageUrl && <Row label="Page" value={c.visitor.pageUrl} />}
      {c.contactId && <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => navigate(`/helpdesk/customers/${c.contactId}`)}>Open 360° profile</Button>}

      <Divider sx={{ my: 1.5 }} />
      <SmallLabel sx={{ mb: 1 }}>CRM information</SmallLabel>
      {crm ? (
        <>
          <Row label="Tickets" value={`${crm.stats.totalTickets} (${crm.stats.openTickets} open)`} />
          <Row label="Leads / deals" value={crm.deals.length} />
          <Row label="Calls" value={crm.calls.length} />
          <Row label="Emails" value={crm.emailCount} />
          <Row label="Chats" value={crm.conversations.length} />
          <Row label="Open tasks" value={crm.tasks.length} />
          <Row label="Avg CSAT" value={crm.stats.avgCsat ?? "—"} />
        </>
      ) : <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{c.contactId ? "Loading…" : "Not linked to a contact"}</Typography>}

      {c.otherOpenTickets?.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <SmallLabel sx={{ mb: 1 }}>Other open tickets</SmallLabel>
          {c.otherOpenTickets.map((o) => (
            <Stack key={o._id} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <Chip size="small" label={o.key} onClick={() => navigate(`/helpdesk/tickets/${o._id}`)} />
              <Typography noWrap sx={{ fontSize: TYPE.micro, flexGrow: 1 }}>{o.subject}</Typography>
              {ctx.can("tickets.update") && <Button size="small" onClick={async () => { try { await hd.chatAction(c._id, "link-ticket", { ticketId: o._id }); ctx.notify(`Chat linked to ${o.key}`); onLinked(); } catch (err) { ctx.notify(errorMessage(err), "error"); } }}>Link</Button>}
            </Stack>
          ))}
        </>
      )}

      <Divider sx={{ my: 1.5 }} />
      <SmallLabel sx={{ mb: 1 }}>Current ticket</SmallLabel>
      {t ? (
        <>
          <Row label="Ticket" value={t.key} />
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <StatusBadge status={t.status} /><PriorityBadge priority={t.priority} /><SlaBadge ticket={t} compact />
          </Stack>
          <Row label="Category" value={t.category?.name} />
        </>
      ) : <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Created with the first message</Typography>}

      <Divider sx={{ my: 1.5 }} />
      <SmallLabel sx={{ mb: 1 }}>AI information</SmallLabel>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {(c.ai?.intent || t?.ai?.intent) && <Chip size="small" label={c.ai?.intent || t?.ai?.intent} />}
        <SentimentChip sentiment={c.ai?.sentiment || t?.ai?.sentiment} />
        {c.language && <Chip size="small" label={c.language} />}
      </Stack>
      {c.ai?.handoffReason && <Row label="Handoff reason" value={c.ai.handoffReason.replace(/_/g, " ")} />}
      {c.ai?.replies > 0 && <Row label="AI replies" value={`${c.ai.replies} (${c.ai.failedAttempts} unanswered)`} />}
      {t?.ai?.summary && <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{t.ai.summary}</Typography>}
      {c.transfers?.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <SmallLabel sx={{ mb: 1 }}>Transfers</SmallLabel>
          {c.transfers.map((x, i) => <Typography key={i} sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{x.by?.name} → {x.to?.name || "team"}{x.reason ? ` (${x.reason})` : ""} · {formatRelative(x.at)}</Typography>)}
        </>
      )}
    </GlassCard>
  );
}

function TransferDialog({ onClose, onDone }) {
  const ctx = useHd();
  const [toMemberId, setMember] = useState("");
  const [toTeamId, setTeam] = useState("");
  const [reason, setReason] = useState("");
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Transfer conversation</DialogTitle>
      <DialogContent dividers>
        <TextField select fullWidth label="To agent" value={toMemberId} onChange={(e) => setMember(e.target.value)} sx={{ mb: 2 }}>
          <MenuItem value="">—</MenuItem>
          {ctx.agents.filter((a) => String(a.memberId) !== String(ctx.me.id)).map((a) => (
            <MenuItem key={a.memberId} value={a.memberId}><Stack direction="row" spacing={1} alignItems="center"><PresenceDot presence={a.presence} /><span>{a.name}</span></Stack></MenuItem>
          ))}
        </TextField>
        <TextField select fullWidth label="Or to team" value={toTeamId} onChange={(e) => setTeam(e.target.value)} sx={{ mb: 2 }}>
          <MenuItem value="">—</MenuItem>
          {ctx.teams.filter((t) => t.isActive).map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
        </TextField>
        <TextField fullWidth label="Reason (internal)" value={reason} onChange={(e) => setReason(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={!toMemberId && !toTeamId} onClick={() => onDone({ toMemberId: toMemberId || undefined, toTeamId: toTeamId || undefined, reason })}>Transfer</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function LiveChat() {
  return (
    <HelpdeskPage flush>
      <Chat />
    </HelpdeskPage>
  );
}
